import React from "react"
import { compose } from "recompact"
import defaultTo from "ramda/es/defaultTo"
import composeF from "ramda/es/compose"
import moment from "moment"
import cn from "classnames"
import { Box } from "@infotech/uikit-grid"
import { Flex } from "src/components/Grid"
import { WarningOutlineIcon, TrashIcon } from "@infotech/uikit-icons"
import EditIcon from "src/icons/edit.svg"
import allResourceTypes from "src/hocs/allResourceTypes"
import allEventTypes from "src/hocs/allEventTypes"
import allPlans from "src/hocs/allPlans"
import allPlanTypes from "src/hocs/allPlanTypes"
import {
  filterConstraintsByCategory,
  findConstraintTypeForConstraint,
  categoryAPI,
  constraintAPI,
  constraintTypeAPI,
  argumentTypes,
  violationLevels,
} from "../helpers"
import CategoryList from "../CategoryList"
import { ANY_OPTION } from "../ConstraintForm"
import styles from "./styles.css"

const RESOURCE_TYPE_COLLECTION = `RESOURCE_TYPE_COLLECTION`
const EVENT_TYPE_COLLECTION = `EVENT_TYPE_COLLECTION`
const PLAN_COLLECTION = `PLAN_COLLECTION`
const PLAN_TYPE_COLLECTION = `PLAN_TYPE_COLLECTION`

const ANY = `Любой`

const ConstraintOption = props => {
  const { label, icon, onEditClick, onDeleteClick, hideButtons } = props

  const buttons = hideButtons ? null : (
    <Flex className={styles.constraintOptionButtons}>
      <Box loose w="2md" h="2md" onClick={onEditClick}>
        <EditIcon className={cn(styles.EditIcon, styles.Icons)} />
      </Box>
      <Box pl="1md" onClick={onDeleteClick} w="24px">
        <TrashIcon className={styles.Icons} />
      </Box>
    </Flex>
  )

  return (
    <li className={styles.constraintOption}>
      <Box flex justify="space-between" align="center" bb pl="2md">
        <Flex align="center">
          {icon}
          <span className={styles.labelSpan}>{label}</span>
        </Flex>
        {buttons}
      </Box>
    </li>
  )
}

const getLabelForArgumentValue = (value, argument, collections) => {
  const {
    TIMESTAMP,
    SELECT,
    RESOURCE_TYPE,
    EVENT_TYPE,
    PLAN,
    PLAN_TYPE,
  } = argumentTypes
  const argumentType = constraintTypeAPI.getArgumentType(argument)
  const defaultName = defaultTo({ name: value })

  switch (argumentType) {
    case SELECT:
      return constraintTypeAPI
        .getArgumentOptions(argument)
        .find(option => option.value === value).label
    case TIMESTAMP:
      return moment(value).format(`DD.MM.YYYY HH:mm`)
    case RESOURCE_TYPE:
      return (
        defaultName(
          collections[RESOURCE_TYPE_COLLECTION].find(
            one => String(one.id) === value,
          ),
        ).name || ANY
      )
    case EVENT_TYPE:
      return (
        defaultName(
          collections[EVENT_TYPE_COLLECTION].find(
            one => String(one.id) === value,
          ),
        ).name || ANY
      )
    case PLAN:
      return (
        defaultName(
          collections[PLAN_COLLECTION].find(one => String(one.id) === value),
        ).name || ANY
      )
    case PLAN_TYPE:
      return (
        defaultName(
          collections[PLAN_TYPE_COLLECTION].find(
            one => String(one.id) === value,
          ),
        ).name || ANY
      )
    default:
      return value
  }
}

const getLabelForViolationLevel = violationLevel => {
  const { ERROR, WARNING } = violationLevels

  switch (violationLevel) {
    case ERROR:
      return `ошибку`
    case WARNING:
      return `предупреждение`
    default:
      return violationLevel
  }
}

const ArgumentValue = ({ label }) => (
  <span className={styles.ArgumentValue}>&nbsp;«{label}»</span>
)

const reduceArgumentsToNodeArray = (args, constraint, collections) =>
  args.reduce((nodes, argument) => {
    const argumentLabel = constraintTypeAPI.getArgumentLabel(argument)

    let argumentName
    let argumentValue
    const isArgument = argument.elementType === `ARGUMENT`
    if (isArgument) {
      argumentName = constraintTypeAPI.getArgumentName(argument)
      argumentValue = constraintAPI.getValue(constraint, argumentName)
    }

    return [
      ...nodes,
      argumentLabel ? ` ${argumentLabel}` : null,
      isArgument ? (
        <ArgumentValue
          key={argumentName}
          label={getLabelForArgumentValue(argumentValue, argument, collections)}
        />
      ) : null,
    ]
  }, [])

const getViolationIconColor = violationLevel => {
  const { ERROR, WARNING } = violationLevels

  switch (violationLevel) {
    case ERROR:
      return `#fc1420`
    case WARNING:
      return `#fcd951`
    default:
      return `#000`
  }
}

const mapConstraintsToOptions = ({
  constraints,
  constraintTypes,
  clickEditHandler,
  clickDeleteHandler,
  displayViolations,
  collections,
  hideButtons,
}) => {
  const getLabel = constraint => {
    const getFirstPart = () => {
      const constraintType = findConstraintTypeForConstraint(
        constraintTypes,
        constraint,
      )
      const args = constraintTypeAPI.getForm(constraintType)

      return args.length
        ? reduceArgumentsToNodeArray(args, constraint, collections)
        : [constraintTypeAPI.getName(constraintType)]
    }

    return [
      ...getFirstPart(),
      ...(displayViolations
        ? [
            `, выводить`,
            <ArgumentValue
              key="violationLevel"
              label={getLabelForViolationLevel(
                constraintAPI.getViolationLevel(constraint),
              )}
            />,
          ]
        : []),
    ]
  }

  return constraints.map(constraint => (
    <ConstraintOption
      key={constraintAPI.getId(constraint)}
      icon={
        displayViolations ? (
          <div className={styles.violationIcon}>
            <WarningOutlineIcon
              color={getViolationIconColor(
                constraintAPI.getViolationLevel(constraint),
              )}
            />
          </div>
        ) : null
      }
      label={getLabel(constraint)}
      onEditClick={() => clickEditHandler(constraint)}
      onDeleteClick={() => clickDeleteHandler(constraint)}
      hideButtons={hideButtons}
    />
  ))
}

export default compose(
  allResourceTypes({ to: `resourceTypeList` }),
  allEventTypes({ to: `eventTypeList` }),
  allPlans({
    to: `planList`,
    query: { limit: -1, orderBy: `name asc`, offset: 0 },
  }),
  allPlanTypes({
    to: `planTypesList`,
    query: { limit: -1, orderBy: `name asc`, offset: 0 },
  }),
)(props => {
  const {
    constraints,
    constraintTypes,
    onEditClick,
    onDeleteClick,
    useGroups,
    categories,
    onGroupClick,
    expandedCategories,
    displayViolations,
    resourceTypeList,
    eventTypeList,
    planList,
    planTypesList,
    isDisabled,
  } = props

  // Reducing the number of args
  const mapConstraints = constraints =>
    mapConstraintsToOptions({
      constraints,
      constraintTypes,
      displayViolations,
      clickEditHandler: onEditClick,
      clickDeleteHandler: onDeleteClick,
      // The collections are needed to display entities names
      collections: {
        [RESOURCE_TYPE_COLLECTION]: [ANY_OPTION, ...resourceTypeList.data],
        [EVENT_TYPE_COLLECTION]: [ANY_OPTION, ...eventTypeList.data],
        [PLAN_COLLECTION]: [ANY_OPTION, ...planList.data],
        [PLAN_TYPE_COLLECTION]: [ANY_OPTION, ...planTypesList.data],
      },
      hideButtons: isDisabled,
    })

  const filterConstraint = category =>
    filterConstraintsByCategory(constraints, constraintTypes, category)

  const list = useGroups ? (
    <CategoryList
      categories={categories}
      expandedCategories={expandedCategories}
      onGroupClick={onGroupClick}
      renderCategoryLabel={category =>
        `${categoryAPI.getName(category)} (${
          filterConstraint(category).length
        })`
      }
      renderCategoryChildren={category => (
        <ul className={styles.UnderListStyles}>
          {composeF(mapConstraints, filterConstraint)(category)}
        </ul>
      )}
    />
  ) : (
    <ul className={styles.UnderListStyles}>{mapConstraints(constraints)}</ul>
  )

  return <div>{list}</div>
})
